/**
 * Landing Page
 * 
 * Public-facing landing page showcasing Brynlore and allowing world selection
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './LandingPage.css';
import './WorldSelectionScreen.css';

interface WorldOption {
  id: string;
  name: string;
  description: string;
  imagePath: string;
  isActive: boolean;
  comingSoon?: boolean;
}

const AVAILABLE_WORLDS: WorldOption[] = [
  {
    id: 'folklore',
    name: 'Folk World',
    description: 'Folklore and whispered legends',
    imagePath: '/assets/backgrounds/folkworld-entry.png',
    isActive: true,
  },
  {
    id: 'lovecraft',
    name: 'Lovecraft World',
    description: 'Cosmic horror and eldritch mysteries',
    imagePath: '/assets/backgrounds/lovecraft-entry.png',
    isActive: false,
    comingSoon: true,
  },
  {
    id: 'anime',
    name: 'Anime World',
    description: 'Where the mundane and the magical coexist harmoniously',
    imagePath: '/assets/backgrounds/anime-entry.png',
    isActive: false,
    comingSoon: true,
  },
  {
    id: 'sovereign',
    name: 'Sovereign World',
    description: 'A billion kings with no subjects',
    imagePath: '/assets/backgrounds/sovereign-entry.png',
    isActive: false,
    comingSoon: true,
  },
];

export function LandingPage(): React.ReactElement {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  const handleWorldClick = (worldId: string, isActive: boolean) => {
    if (!isActive) return; // Don't allow clicking on inactive worlds
    
    if (!user) {
      // Not logged in, redirect to login with return path
      navigate(`/login?redirectTo=/play/${worldId}`);
    } else {
      // Logged in, go directly to the world
      navigate(`/play/${worldId}`);
    }
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Set background image via inline styles (CSS can't resolve public assets)
  const bannerStyle: React.CSSProperties = {
    backgroundImage: `url('/assets/backgrounds/auth-background.png')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  };

  return (
    <div className="landing-page">
      {/* Header with login/logout */}
      <header className="landing-header">
        <div className="landing-header-content">
          <h1 className="landing-logo">Brynlore</h1>
          {user ? (
            <div className="landing-user-section">
              <span className="landing-user-name">{user.displayName || user.email}</span>
              <button onClick={handleLogout} className="landing-logout-btn">
                Sign Out
              </button>
            </div>
          ) : (
            <button onClick={handleLogin} className="landing-login-btn">
              Sign In / Sign Up
            </button>
          )}
        </div>
      </header>

      {/* Banner - Full Width Background Image */}
      <div className="landing-banner" style={bannerStyle}>
        <div className="landing-banner-overlay"></div>
      </div>

      {/* Content Section */}
      <div className="landing-content">
        <h2 className="landing-tagline">Start the journey, write the legend</h2>
        
        <p className="landing-description">
          Enter a world where every step you take shapes the story. Every Brynlore world is a living, 
          breathing realm powered by AI-generated narratives that evolve with your choices. 
          Explore hex-based maps, battle fierce creatures, uncover ancient lore, and forge 
          your own path through an ever-expanding saga.
        </p>

        {/* World Selection */}
        <div className="landing-worlds-section">
          <h2 className="landing-worlds-title">Choose Your World</h2>
          <div className="landing-worlds-grid">
            {AVAILABLE_WORLDS.map((world) => (
              <div
                key={world.id}
                className={`world-card ${!world.isActive ? 'world-card--disabled' : ''}`}
                onClick={() => handleWorldClick(world.id, world.isActive)}
                style={{ cursor: world.isActive ? 'pointer' : 'default' }}
              >
                <div className="world-card__image-container">
                  <img
                    src={world.imagePath}
                    alt={world.name}
                    className="world-card__image"
                  />
                  {world.comingSoon && (
                    <div className="world-card__badge">Coming Soon</div>
                  )}
                </div>
                <div className="world-card__content">
                  <h2 className="world-card__title">{world.name}</h2>
                  <p className="world-card__description">{world.description}</p>
                  {world.isActive && (
                    <button className="world-card__button">
                      Enter World
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="landing-footer">
        <p>&copy; 2025 Brynlore. All rights reserved.</p>
      </footer>
    </div>
  );
}

